/**
 * AssetDrawerHeader Component
 *
 * Displays the asset header section including identifier, group, status, tags, and actions.
 * Pure presentation component - all state management handled by parent.
 */

import { ShareIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';

import { Button } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { DateStatusBox } from '@/components/DateStatusBox';
import { Loader } from '@/components/Loader';
import { OverflowText } from '@/components/OverflowText';
import { ScanStatus } from '@/components/ScanStatusContainer';
import { useEntitlements } from '@/hooks/useEntitlements';
import { SimpleTags } from '@/sections/AddTag';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { Asset } from '@/types';
import { getDisplayIdentifierWithFallback } from '@/utils/displayIdentifier.util';

export interface AssetDrawerHeaderProps {
  asset: Asset;
  entityIdentifier: string;
  entityGroup: string;
  assetsStatus: 'pending' | 'error' | 'success';
  tags?: string[];
  onRemoveTag?: (tag: string) => void;
}

/**
 * AssetDrawerHeader renders the top section of the asset drawer with:
 * - Asset identifier with copy functionality
 * - Share button for URL copying
 * - Entity group display
 * - Asset status badge
 * - Tags display with removal functionality
 * - Scan status indicator
 *
 * This is a pure presentation component with no internal state.
 */
export function AssetDrawerHeader({
  asset,
  entityIdentifier,
  entityGroup,
  assetsStatus,
  tags,
  onRemoveTag,
}: AssetDrawerHeaderProps) {
  const { canWriteAssets } = useEntitlements();

  const handleShareClick = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Copied URL to clipboard');
  };

  const displayIdentifier = getDisplayIdentifierWithFallback(
    asset,
    entityIdentifier
  );

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Row 1: Full-width identifier with share button */}
      <div className="flex items-center gap-2">
        <CopyToClipboard
          alwaysShowCopyIcon
          className="min-w-0 flex-1 gap-2 overflow-hidden [&_svg]:ml-0"
        >
          <OverflowText
            className="text-xl font-bold text-nowrap"
            text={displayIdentifier}
          />
        </CopyToClipboard>
        <Button
          styleType="icon"
          className="shrink-0 p-0"
          startIcon={<ShareIcon className="text-chariot-text-primary size-4" />}
          onClick={handleShareClick}
          data-testid="share-asset"
          label={''}
        />
      </div>

      {/* Row 2: Group/Tags (left) + Status boxes (right) */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-hidden">
          <CopyToClipboard
            alwaysShowCopyIcon
            className="gap-2 overflow-hidden [&_svg]:ml-0"
          >
            <OverflowText className="text-nowrap" text={entityGroup || ''} />
          </CopyToClipboard>
          {tags && tags.length > 0 && onRemoveTag && (
            <SimpleTags
              tags={tags}
              onRemove={canWriteAssets ? onRemoveTag : undefined}
              variant="compact"
            />
          )}
        </div>

        <div className="flex shrink-0 items-start gap-2">
          <ScanStatus asset={asset} />
          <div className="bg-chariot-background-secondary flex h-[88px] w-[153px] shrink-0 flex-col items-center justify-center gap-1 rounded-md px-4">
            <p className="text-chariot-text-primary text-xs font-semibold">
              Asset Status
            </p>
            <Loader
              isLoading={assetsStatus === 'pending'}
              className="h-[26px] w-[85px] rounded-full"
            >
              <div className="flex h-[26px] w-[85px] items-center justify-center">
                {asset && asset.key && <AssetStatusComponent asset={asset} />}
              </div>
            </Loader>
          </div>
          <DateStatusBox firstSeen={asset.created} lastSeen={asset.visited} />
        </div>
      </div>
    </div>
  );
}
